import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REPUB_CONFIG } from '@cx-web/repub-config';
import { trackEvent } from '@cx-web/utils/dataLayer';
import { Button } from '@fpsm/cxy-components/Button';
import { Checkbox } from '@fpsm/cxy-components/Checkbox';
import { Input } from '@fpsm/cxy-components/Input';
import { useFetcher } from '@remix-run/react';

import { type Repub } from '@cx-modules/constants';

import { CX_LOGIN_SUCCESS } from '~/apps/product-plus/events/CXLoginSuccessEvent';
import { type View } from '~/apps/product-plus/providers/auth-modal-provider';
import { trackSignupFailure, trackSignupSuccess } from '~/utils/tracking';

import { actions, type AuthResponse, events, getFieldErrorMessage, views } from '../../types';

import styles from './styles/SignupForm.module.css';

export type SignupFormProps = {
  close: () => void;
  openAtView: (view: View) => void;
  repub: Repub;
  repubPath: string;
};

export const SignupForm = ({ close, openAtView, repub, repubPath }: SignupFormProps) => {
  const fetcher = useFetcher<AuthResponse>();
  const signupSuccessRef = useRef(false);
  const [showErrorState, setShowErrorState] = useState(false);

  const { t } = useTranslation(['label']);

  const data = fetcher.data;
  const hasErrorMessage = data?.message && !data?.success;

  // handle local error
  useEffect(() => {
    if (fetcher.state === 'idle' && (data?.fieldErrors || hasErrorMessage)) {
      setShowErrorState(true);
    }
  }, [data?.fieldErrors, hasErrorMessage, data?.message, fetcher.state]);

  // handle api success
  useEffect(() => {
    if (data?.success && !signupSuccessRef.current) {
      signupSuccessRef.current = true;

      trackSignupSuccess('auth_modal', { repub, userId: data.userId! });

      window.dispatchEvent(new CustomEvent(CX_LOGIN_SUCCESS, { detail: data }));

      close();
    }
  }, [close, data, openAtView, repub]);

  // handle api error
  const email = fetcher.formData?.get('email');
  useEffect(() => {
    if (hasErrorMessage && email && data) {
      trackSignupFailure('auth_modal', {
        email: email.toString(),
        error: data.message ?? '',
        repub,
      });
    }
  }, [hasErrorMessage, email, data, repub]);

  const onChange = () => {
    setShowErrorState(false);
  };

  return (
    <fetcher.Form
      action={`${repubPath}/plus/auth/signup`}
      className={styles.signUpForm}
      data-testid="signup-form"
      method="post"
      onSubmit={() => {
        trackEvent(events.ACCOUNT_CREATED_ATTEMPT, {
          eventAction: actions.SIGN_UP,
          method: REPUB_CONFIG[repub]?.tracking.authProvider,
          view: views.SIGN_UP,
        });
      }}
    >
      <Input
        aria-describedby="email-error"
        autoComplete="email"
        className={styles.input}
        errors={
          showErrorState ? ([getFieldErrorMessage(data?.fieldErrors?.email)].filter(Boolean) as string[]) : undefined
        }
        id="email"
        key="email"
        name="email"
        onChange={onChange}
        placeholder="Email"
        type="email"
      />
      <Input
        aria-describedby="password-error"
        autoComplete="new-password"
        id="password"
        key="password"
        name="password"
        onChange={onChange}
        placeholder="Password"
        type="password"
      />
      <Input
        aria-describedby="confirmPassword-error"
        autoComplete="new-password"
        errors={
          showErrorState
            ? ([
                getFieldErrorMessage(data?.fieldErrors?.password),
                getFieldErrorMessage(data?.fieldErrors?.confirmPassword),
              ].filter(Boolean) as string[])
            : undefined
        }
        id="confirmPassword"
        key="confirmPassword"
        name="confirmPassword"
        onChange={onChange}
        placeholder="Confirm Password"
        type="password"
      />
      {hasErrorMessage && showErrorState && (
        <div className={styles.errorMessage} id="generic-error">
          {data.message}
        </div>
      )}
      {REPUB_CONFIG[repub]?.plus?.auth.showAgreeTermsAsCheckbox &&
        showErrorState &&
        getFieldErrorMessage(data?.fieldErrors?.agreeTerms) && (
          <div className={styles.agreeTermsError} id="agreeTerms-error">
            {getFieldErrorMessage(data?.fieldErrors?.agreeTerms)}
          </div>
        )}
      <div className={styles.buttonSection}>
        <Button isLoading={fetcher.state !== 'idle'} size="large" type="submit" variant="secondary">
          {t('CX_AUTH_SIGNUP')}
        </Button>
      </div>
      {REPUB_CONFIG[repub]?.plus?.auth.showAgreeTermsAsCheckbox && (
        <div className={styles.termsContainer}>
          <div className={styles.termsCheckboxRow}>
            <Checkbox id="agreeTerms" name="agreeTerms" onChange={onChange} value="true" />
            <p
              className={
                showErrorState && getFieldErrorMessage(data?.fieldErrors?.agreeTerms)
                  ? styles.marketingConsentTextError
                  : styles.marketingConsentText
              }
            >
              {t('CX_SOCIAL_CONFIRM_AGE')}{' '}
              <a
                className={
                  showErrorState && getFieldErrorMessage(data?.fieldErrors?.agreeTerms)
                    ? styles.termsLinkError
                    : styles.termsLink
                }
                href={REPUB_CONFIG[repub]?.plus?.auth.termsAndConditionsUrl(repubPath)}
              >
                {t('CX_AUTH_TS_AND_CS')}
              </a>{' '}
              and{' '}
              <a
                className={
                  showErrorState && getFieldErrorMessage(data?.fieldErrors?.agreeTerms)
                    ? styles.termsLinkError
                    : styles.termsLink
                }
                href={REPUB_CONFIG[repub]?.plus?.auth.privacyPolicyUrl(repubPath)}
              >
                {t('CX_AUTH_PRIVACY_POLICY')}
              </a>
              .
            </p>
          </div>
        </div>
      )}
      {REPUB_CONFIG[repub]?.plus?.auth.showMarketingConsent && (
        <Checkbox id="marketingConsent" name="marketingConsent" value="true">
          <p className={styles.marketingConsentText}>
            Send me exclusive tips and offers from {REPUB_CONFIG[repub]?.plus?.name}
          </p>
        </Checkbox>
      )}
      {!REPUB_CONFIG[repub]?.plus?.auth.showAgreeTermsAsCheckbox && (
        <p className={styles.termsText}>
          {t('CX_SOCIAL_CONFIRM_AGE')}{' '}
          <a href={REPUB_CONFIG[repub]?.plus?.auth.termsAndConditionsUrl(repubPath)}>{t('CX_AUTH_TS_AND_CS')}</a> and{' '}
          <a href={REPUB_CONFIG[repub]?.plus?.auth.privacyPolicyUrl(repubPath)}>{t('CX_AUTH_PRIVACY_POLICY')}</a>.
        </p>
      )}
    </fetcher.Form>
  );
};

SignupForm.displayName = 'SignupForm';
